import { apiGet, apiGetBlob, apiPost, apiPatch, apiPut, apiUpload, apiDelete } from '../api-client'

// ---- Types ----

export interface PlanOptions {
  combine_nearby_plugs: boolean
  combine_threshold_ft: number
  surface_plug_bottom_ft?: number
}

export interface EventComplianceFlag {
  day_number: number
  event_index: number
  event_type: string
  severity: 'violation' | 'warning' | 'info'
  rule_id: string
  rule_label: string
  detail: string
  citation: string
  field_name: string
}

export interface EventComplianceFlags {
  jurisdiction: string
  policy_id: string
  checked_at: string
  summary: {
    total_events_checked: number
    violations: number
    warnings: number
    info: number
  }
  flags: EventComplianceFlag[]
}

export type W3WizardStatus =
  | 'created' | 'uploading' | 'parsing' | 'parsed'
  | 'reconciled' | 'justifying' | 'ready'
  | 'generating' | 'completed' | 'abandoned'
  | 'importing_plan' | 'plan_imported' | 'plan_verified'

export interface UploadedDocument {
  file_name: string
  file_type: string
  storage_key: string
  uploaded_at: string
  size_bytes: number
  category?: string
  is_excluded?: boolean
}

export interface JustificationEntry {
  note: string
  resolved: boolean
  resolved_by: string
  resolved_at: string | null
  ai_suggested?: boolean
  source_type?: string
  confidence?: number
  // Override fields
  depth_top_ft_override?: number | null
  depth_bottom_ft_override?: number | null
  sacks_override?: number | null
  excluded?: boolean
}

export interface CasingString {
  string: string
  top_ft: number | null
  size_in: number
  bottom_ft: number
  hole_size_in: number
  cement_top_ft?: number | null
  id_in?: number
  removed_to_depth_ft?: number | null
}

export interface FormationTop {
  top_ft: number
  base_ft?: number | null
  formation: string
}

export interface Perforation {
  top_ft: number
  bottom_ft: number
}

export interface Tubing {
  top_ft: number | null
  size_in: number
  bottom_ft: number
}

export interface ExistingTool {
  source?: string
  tool_type?: string
  type?: string
  material_description?: string
  top_ft?: number
  bottom_ft?: number
  depth_ft?: number
  open_hole?: boolean
  notes?: string | null
  description?: string
}

export interface WellGeometry {
  casing_strings: CasingString[]
  formation_tops: FormationTop[]
  production_perforations: Perforation[]
  perforations?: Perforation[]
  tubing: Tubing[]
  existing_tools?: ExistingTool[]
  mechanical_equipment?: ExistingTool[]
  liner?: Array<{ size_in?: number; top_ft?: number; bottom_ft?: number }>
  kop_ft?: number
  historic_cement_jobs?: Array<{
    job_type?: string
    interval_top_ft?: number
    interval_bottom_ft?: number
    cement_top_ft?: number
  }>
}

export interface PlanStep {
  step_number: number
  step_type: string
  depth_top_ft: number | null
  depth_bottom_ft: number | null
  sacks: number | null
  cement_class: string | null
  description: string | null
  category: string
}

export interface PlanFormation {
  formation_name: string
  top_ft: number | null
}

export interface PlanCasing {
  string_type: string
  size_in: number | null
  weight_ppf: number | null
  grade: string | null
  top_ft: number | null
  bottom_ft: number | null
  shoe_depth_ft: number | null
  hole_size_in: number | null
  cement_top_ft: number | null
}

export interface PlanPerforation {
  depth_top_ft: number | null
  depth_bottom_ft: number | null
  formation_name: string | null
  status: string
}

export interface PlanVerificationPayload {
  well_header: Record<string, string | number | boolean | null>
  steps: PlanStep[]
  formations: PlanFormation[]
  casing_record: PlanCasing[]
  existing_perforations: PlanPerforation[]
}

export interface W3WizardSession {
  id: string
  well: number | null
  plan_snapshot: string | null
  plan_snapshot_well_geometry: WellGeometry | null
  plan_snapshot_well_header: Record<string, string> | null
  plan_snapshot_payload?: {
    well_header: Record<string, string | number | boolean | null>
    steps: PlanStep[]
    formations: PlanFormation[]
    casing_record: PlanCasing[]
    existing_perforations: PlanPerforation[]
  } | null
  w3_form: number | null
  tenant_id: string | null
  workspace: number | null
  api_number: string
  jurisdiction: 'TX' | 'NM'
  form_type: 'w3' | 'sundry'
  status: W3WizardStatus
  current_step: number
  uploaded_documents: UploadedDocument[]
  parse_result: Record<string, unknown>
  reconciliation_result: Record<string, unknown>
  justifications: Record<string, JustificationEntry>
  w3_generation_result: Record<string, unknown>
  celery_task_id: string
  plan_import_task_id: string
  created_by: string
  created_at: string
  updated_at: string
  last_accessed_at: string
  event_compliance_flags?: EventComplianceFlags | null
}

export interface W3WizardListItem {
  id: string
  api_number: string
  status: W3WizardStatus
  current_step: number
  created_by: string
  created_at: string
  updated_at: string
}

export interface TaskStatusResponse {
  task_id: string
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE'
  session_status: string
  result?: Record<string, unknown>
}

// ---- API Functions ----

export async function createSession(apiNumber: string, workspaceId?: number): Promise<W3WizardSession> {
  return apiPost<W3WizardSession>('/api/w3-wizard/', { api_number: apiNumber, workspace_id: workspaceId })
}

export async function listSessions(params?: { status?: string; workspace?: number }): Promise<W3WizardListItem[]> {
  const query = new URLSearchParams()
  if (params?.status) query.set('status', params.status)
  if (params?.workspace) query.set('workspace', String(params.workspace))
  const qs = query.toString()
  return apiGet<W3WizardListItem[]>(`/api/w3-wizard/${qs ? `?${qs}` : ''}`)
}

export async function getSession(sessionId: string): Promise<W3WizardSession> {
  return apiGet<W3WizardSession>(`/api/w3-wizard/${sessionId}/`)
}

export async function abandonSession(sessionId: string): Promise<void> {
  return apiDelete(`/api/w3-wizard/${sessionId}/`)
}

export async function uploadTickets(
  sessionId: string,
  files: File[]
): Promise<{ uploaded_count: number; documents: UploadedDocument[]; session_status: string }> {
  const formData = new FormData()
  files.forEach((f) => formData.append('files', f))
  formData.append('category', 'tickets')
  return apiUpload(`/api/w3-wizard/${sessionId}/upload/`, formData)
}

export interface PlanImportStatusResponse {
  task_id: string
  status: 'PENDING' | 'STARTED' | 'SUCCESS' | 'FAILURE'
  session_status: string
  plan_snapshot: string | null
  error?: string
  reasons?: string[]
}

export async function uploadPlan(
  sessionId: string,
  file: File
): Promise<{ uploaded_count: number; documents: UploadedDocument[]; session_status: string; plan_import_task_id: string }> {
  const formData = new FormData()
  formData.append('files', file)
  formData.append('category', 'plan')
  return apiUpload(`/api/w3-wizard/${sessionId}/upload/`, formData)
}

export async function toggleDocumentExclusion(
  sessionId: string,
  storageKey: string,
  isExcluded: boolean
): Promise<{ documents: UploadedDocument[] }> {
  return apiPatch<{ documents: UploadedDocument[] }>(
    `/api/w3-wizard/${sessionId}/documents/toggle-exclusion/`,
    { storage_key: storageKey, is_excluded: isExcluded }
  )
}

export async function pollPlanImportStatus(sessionId: string): Promise<PlanImportStatusResponse> {
  return apiGet<PlanImportStatusResponse>(`/api/w3-wizard/${sessionId}/plan-import-status/`)
}

export async function triggerParse(sessionId: string): Promise<TaskStatusResponse> {
  return apiPost<TaskStatusResponse>(`/api/w3-wizard/${sessionId}/parse/`)
}

export async function pollParseStatus(sessionId: string): Promise<TaskStatusResponse> {
  return apiGet<TaskStatusResponse>(`/api/w3-wizard/${sessionId}/parse-status/`)
}

export async function triggerReconciliation(
  sessionId: string,
  planOptions?: PlanOptions
): Promise<TaskStatusResponse> {
  return apiPost<TaskStatusResponse>(
    `/api/w3-wizard/${sessionId}/reconciliation/`,
    planOptions ? { plan_options: planOptions } : undefined
  )
}

export async function getReconciliation(sessionId: string): Promise<Record<string, unknown>> {
  return apiGet(`/api/w3-wizard/${sessionId}/reconciliation/`)
}

export async function saveJustifications(
  sessionId: string,
  justifications: Record<string, JustificationEntry>
): Promise<{ justifications: Record<string, JustificationEntry>; session_status: string }> {
  return apiPatch(`/api/w3-wizard/${sessionId}/justifications/`, { justifications })
}

export async function generateW3(sessionId: string): Promise<TaskStatusResponse> {
  return apiPost<TaskStatusResponse>(`/api/w3-wizard/${sessionId}/generate-w3/`)
}

export async function getW3Form(sessionId: string): Promise<Record<string, unknown>> {
  return apiGet(`/api/w3-wizard/${sessionId}/w3-form/`)
}

export async function uploadWbdImage(
  sessionId: string,
  imageBlob: Blob
): Promise<{ wbd_image_path: string }> {
  const formData = new FormData()
  formData.append('image', imageBlob, 'wbd.png')
  return apiUpload<{ wbd_image_path: string }>(`/api/w3-wizard/${sessionId}/upload-wbd-image/`, formData)
}

export async function exportWbdExcel(sessionId: string): Promise<Blob> {
  return apiGetBlob(`/api/w3-wizard/${sessionId}/export-wbd-excel/`)
}

export async function downloadW3Pdf(sessionId: string): Promise<Blob> {
  return apiGetBlob(`/api/w3-wizard/${sessionId}/download-pdf/`)
}

export async function importWbdExcel(
  sessionId: string,
  file: File
): Promise<{ status: string; updated_fields: string[]; warnings: string[] }> {
  const formData = new FormData()
  formData.append('excel_file', file)
  return apiUpload(`/api/w3-wizard/${sessionId}/import-wbd-excel/`, formData)
}

export async function submitPlanVerification(
  sessionId: string,
  payload: PlanVerificationPayload
): Promise<W3WizardSession> {
  return apiPut<W3WizardSession>(`/api/w3-wizard/${sessionId}/plan-verification/`, payload)
}
